import { SObjectId } from '../mdl/s-object';
import { $cdn } from '../y/cdn';
import { $log } from '../y/debug';
import { yDefer } from '../y/util';
import { $el } from './elements';
import { UII18NValue, UIOrientation, UIValue } from './types';
import { UIMultiViewSpec, UIViewAddSpec, UIViewSpec } from './view';

export function uiMouseDistance(a: MouseEvent, b: MouseEvent, o?: UIOrientation) {
    if(!o || o == UIOrientation.Both) {
        const dx = a.pageX - b.pageX, dy = a.pageY - b.pageY;
        return Math.sqrt(dx * dx + dy * dy);
    } else if(o == UIOrientation.H) {
        return Math.abs(a.pageX - b.pageX);
    } else {
        return Math.abs(a.pageY - b.pageY);
    }
}

function addCSS(rules: string, element?: HTMLStyleElement): HTMLStyleElement | undefined {
    element ??= document.createElement('style');
    element.innerHTML = rules;
    document.head.appendChild(element);
    return element;
}

let _global_cursor_rule: HTMLStyleElement | undefined;

export function uiSetGlobalCursor(cursor: string | null) {
    if(cursor) {
        _global_cursor_rule = addCSS(`*{cursor:${cursor}!important}`, _global_cursor_rule);
    } else if(_global_cursor_rule) {
        _global_cursor_rule.remove();
        _global_cursor_rule = undefined;
    }
}

let _global_anchor_rule: HTMLStyleElement | undefined;

export function uiSetOverflowAnchor(on: boolean) {
    if(!on) {
        _global_anchor_rule = addCSS(`*{overflow-anchor: none}`, _global_anchor_rule);
    } else if(_global_anchor_rule) {
        _global_anchor_rule.remove();
        _global_anchor_rule = undefined;
    }
}

export function uiGetElements(what: UIMultiViewSpec): HTMLElement[] {
    if(Array.isArray(what)) {
        return what.map(w => w.ui_elem);
    } else {
        return [what.ui_elem];
    }
}

export function uiIsOverflowing(what: UIViewSpec) {
    const el = what.ui_elem;
    return el.scrollWidth > el.offsetWidth || el.scrollHeight > el.offsetHeight;
}

export const ui_font_accept = ['.otf', '.woff2'];
export const ui_image_mimetypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
export const ui_new_image_mimetypes = ['image/webp', 'image/svg+xml'];
export const ui_spreadsheet_mimetypes = ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];

export function uiMakeFakeUploadInput(callback: (file: FileList) => void, accept: string[] = ui_image_mimetypes, multiple?: boolean) {
    const input = $el.INPUT('file', 'ui-fake-input');
    input.tabIndex = -1;
    input.accept = accept.join(',');
    input.multiple = !!multiple;

    input.addEventListener('change', () => {
        if(input.files && input.files.length) {
            callback(input.files);
            input.value = '';
        }
    });


    (window as any)._fake_upload_inputs = (window as any)._fake_upload_inputs || [];
    (window as any)._fake_upload_inputs.push(input);
    return input;
}

export function uiForceIFrameBlur() {
    const input = $el.INPUT('text', 'ui-fake-input');
    document.body.appendChild(input);
    input.focus();
    input.blur();
    document.body.removeChild(input);
}

export function uiMakeAbsolute(elem: HTMLElement) {
    const offset = -window.scrollY;
    elem.style.position = 'fixed';
    elem.style.zIndex = '50';
    elem.style.top = offset + 'px';
    window.scrollTo(0, 0);
}

export function uiDimension(v: number | string) {
    return typeof v === 'number' ? v + 'px' : v;
}

export function uiMakeValues(keys: string[], sort?: boolean) {
    const values = keys.map((k: string) => ({ key: k, name: k }));
    if(sort)
        values.sort((a, b) => a.name.localeCompare(b.name));
    return values;
}

export function uiMakeValuesFromObject<T extends string>(obj: Record<T, { name: string }>): UIValue<T>[] {
    return Object.keys(obj).map(k => ({ key: k as T, name: obj[k as T].name }));
}

export function uiEntryToValue(entry: [string, string]): UIValue<string> {
    return { key: entry[0], name: entry[1] };
}

export function uiMakeI18NValues<K extends string>(catalog: { [k in K]: string }, keys?: K[], sort?: boolean): UIValue<K>[] {
    const values = (keys || Object.keys(catalog) as K[]).map(k => ({ key: k, name: catalog[k] }));
    if(sort)
        values.sort((a, b) => a.name.localeCompare(b.name));
    return values;
}

export function uiFillI18NValues(catalog: { [k: string]: string }, values: UII18NValue[], sort?: boolean): UIValue[]
export function uiFillI18NValues(catalog: { [k: string]: string }, values: any[], sort?: boolean) {
    for(const v of values) {
        v.name = catalog[v.key];
    }

    if(sort)
        values.sort((a, b) => a.name.localeCompare(b.name));
    return values;
}

const replace_regexp = /\$(\d+)\$/

export function uiReplaceI18N(pattern: string, ...replacements: (UIViewAddSpec | (() => UIViewAddSpec))[]): UIViewAddSpec {
    const split = pattern.split(replace_regexp);

    const result: UIViewAddSpec = [split[0]];

    for(let i = 1; i < split.length; i += 2) {
        const n = parseInt(split[i]);
        if(n > 0 && n <= replacements.length) {
            const replacement = replacements[n - 1];
            result.push(typeof replacement == 'function' ? replacement() : replacement);
        }
        result.push(split[i + 1]);
    }

    return result;
}

export function uiReplaceI18Ns(patterns: string[], ...replacements: (UIViewAddSpec | (() => UIViewAddSpec))[]): UIViewAddSpec[] {
    return patterns.map(p => uiReplaceI18N(p, ...replacements));
}

let locked_ui = 0;
const ui_lock_events = ['mousedown', 'mouseup', 'click', 'mousemove', 'wheel', 'keydown', 'keyup', 'keypress'];
const ui_lock_handler = (ev: Event) => {
    ev.stopImmediatePropagation();
    ev.preventDefault();
}

export function uiLock<T>(promise?: Promise<T>) {
    locked_ui++;

    if(locked_ui === 1) {
        for(const e of ui_lock_events) {
            document.body.addEventListener(e, ui_lock_handler, { capture: true });
        }
    }

    return promise?.finally(uiUnlock);
}

export function uiUnlock(force?: boolean) {
    if(locked_ui <= 0) return;

    if(force)
        locked_ui = 0;
    else
        locked_ui--;

    if(locked_ui <= 0) {
        for(const e of ui_lock_events) {
            document.body.removeEventListener(e, ui_lock_handler, { capture: true });
        }
    }
}

export function uiPreloadImage(image: SObjectId) {
    const url = $cdn.media(image as SObjectId);
    const img = new Image();
    img.src = url;
    const deferred = yDefer<string>();
    img.onload = () => { deferred.resolve(url) };
    img.onerror = () => { deferred.reject() };

    return deferred.promise;
}

interface UIOpenWindowOptions {
    width?: number;
    height?: number;
}

export function uiOpenWindow(href: string, options: UIOpenWindowOptions = {}) {
    const params: string[] = [];
    if(options.width) {
        params.push('width=' + options.width);
        params.push('left=' + Math.round(window.screenX + window.outerWidth / 2 - options.width / 2));
    }
    if(options.height) {
        params.push('height=' + options.height);
        params.push('top=' + Math.round(window.screenY + window.outerHeight / 2 - options.height / 2));
    }

    return window.open(href, '_blank', params.join(','));
}

interface CallbackData<Data> {
    path: string[];
    form: Data;
}

export function uiWaitCallback<Data = { [key: string]: string }>(href: string, options: UIOpenWindowOptions = {}) {
    return new Promise<CallbackData<Data>>((resolve, reject) => {
        const popup = uiOpenWindow(href, options);
        if(popup) {
            const message_listener = (ev: MessageEvent) => {
                $log('callback', 'message!!!!', ev, ev.data);
                if(ev.source === popup) {
                    removeEventListener('message', message_listener);
                    resolve(ev.data);
                    popup.close();
                }
            }

            addEventListener('message', message_listener);
        } else {
            reject('No window');
        }
    });
}

export function uiWaitIframe<Data = { [key: string]: string }>(iframe: HTMLIFrameElement) {
    return new Promise<CallbackData<Data>>((resolve, _reject) => {
        const message_listener = (ev: MessageEvent) => {
            $log('callback', 'got message', ev.source === iframe.contentWindow, ev.data, ev.origin, window.origin);
            if(ev.source === iframe.contentWindow) {
                removeEventListener('message', message_listener);
                resolve(ev.data);
            }
        }

        addEventListener('message', message_listener);
    });
}

export function uiSetPrefixedClass(node: UIViewSpec | Element, prefix: string, value: string | undefined) {
    const list = node.classList;
    const length = list.length;
    prefix += '-';

    for(let i = 0; i < length; i++) {
        const v = list[i];
        if(v.startsWith(prefix)) {
            if(value)
                list.replace(v, prefix + value);
            else
                list.remove(v);
            return;
        }
    }

    if(value)
        list.add(prefix + value);
}

export function uiRemovePrefixedClass(node: UIViewSpec | Element, prefix: string) {
    uiSetPrefixedClass(node, prefix, undefined);
}

export function uiGetPrefixedClass(node: UIViewSpec | Element, prefix: string): string | undefined {
    const list = node.classList;
    const length = list.length;
    prefix += '-';

    for(let i = 0; i < length; i++) {
        const v = list[i];
        if(v.startsWith(prefix))
            return v.substring(prefix.length);
    }

    return;
}

export function uiHasPrefixedClass(node: UIViewSpec | Element, prefix: string, value: string) {
    return node.classList.contains(prefix + '-' + value);
}

export function uiEqualClassList(a: Element, b: Element) {
    const a_list = a.classList;
    const b_list = b.classList;
    if(a_list.length != b_list.length)
        return false;
    for(const c of a_list) {
        if(!b_list.contains(c))
            return false;
    }
    return true;
}

export function uiGetSiblingsBefore(node: Node | null | undefined, inclusive?: boolean): Node[] {
    if(!node) return [];
    const ret = inclusive ? [node] : [];
    while(node = node.previousSibling) {
        ret.unshift(node);
    }
    return ret;
}

export function uiGetSiblingsAfter(node: Node | null | undefined, inclusive?: boolean): Node[] {
    if(!node) return [];
    const ret = inclusive ? [node] : [];
    while(node = node.nextSibling) {
        ret.push(node);
    }
    return ret;
}

export function uiSetScrollId(where: UIViewSpec, id: string) {
    where = where.ui_elem;
    where.classList.add('x-scroll-id');
    where.ui_scroll_id = id;
}

export function uiPlacehold(what: UIViewSpec) {
    const elem = what.ui_elem;
    const width = elem.offsetWidth + 'px';
    const height = elem.offsetHeight + 'px';

    elem.style.width = width;
    elem.style.height = height;

    const styles = getComputedStyle(elem);

    const placeholder = $el.DIV('x-placeholder');
    placeholder.style.width = width;
    placeholder.style.height = height;
    if(styles.marginLeft)
        placeholder.style.marginLeft = styles.marginLeft;
    if(styles.marginRight)
        placeholder.style.marginRight = styles.marginRight;
    if(styles.marginTop)
        placeholder.style.marginTop = styles.marginTop;
    if(styles.marginBottom)
        placeholder.style.marginBottom = styles.marginBottom;

    return placeholder;
}
